<template>
  <div v-if="entityId" class="users-sync">
    <div v-if="otherUsersInfo.length" class="users-sync__other-users v-centerer">
      <div>
        {{ `${$t('message.OTHER_CONNECTED_USERS')}${$t('message.TWO_DOTS')}` }}
      </div>
      <div class="users-sync__other-users__wrapper v-centerer">
        <div v-for="(info, idx) in otherUsersInfo" :key="`${info.user}-${idx}`">
          <UserThumbnail
            v-if="info.user"
            v-quick-title="companyUsers[info.user] ? $t('fullNameFormat', companyUsers[info.user]) : ''"
            :value="info.user"
            fetch-data
            disabled
          />
          <div
            v-else
            v-quick-title="allTeamsPerId[`${info.userTeam}`] ? allTeamsPerId[`${info.userTeam}`].name : ''"
            class="users-sync__other-users__team"
          >
            <Icon>{{ teamMedium }}</Icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * UsersSync. Synchronization handler to display all the users connected to a page at a time.
 * The component displays the thumbnail of all the users connected with the same entity type.
 * The behavior is to send a 'JOINED' message through the socket when UsersSync is created,
 * and a 'LEFT' message when UsersSync is destroyed. (same happens when the ID changes)
 *
 * @vue-prop {Number} entityId - ID of the data where the users connect (eg: ID of the problem / action / GW, ...)
 * @vue-prop {String} entityType - String that must be shared by the users that see each other.
 *                                 eg 'pb-sheet', 'action', 'completed-gw', ...
 */

import { mapGetters } from 'vuex';
// Components
import Icon from '@/components/icon.vue';
import UserThumbnail from '@/components/user-thumbnail.vue';
import { teamMedium } from '@/assets/icons';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';

export default {

  name: 'UsersSync',

  components: {
    Icon,
    UserThumbnail,
  },

  mixins: [mixinSocketHandler],

  props: {
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      teamMedium,
      // Interval to notify other users that the current user is connected
      sendPresenceInterval: null,
      otherUsersInfo: [],
    };
  },

  computed: {
    /** Object: Message to routinely send over the socket */
    socketMsg() {
      return {
        id: this.entityId,
        user: this.currentUser ? this.currentUser.id : null,
        userTeam: this.currentTeam.id,
        msg: SocketMessages.CC_MSGS.SYNC_USER_JOINED,
        type: this.entityType,
        socketCompany: 1,
      };
    },
    ...mapGetters([
      'allTeamsPerId',
      'companyUsers',
    ]),
  },

  watch: {
    entityId() {
      if (this.entityId !== null) {
        this.setupPresenceSocketMsg();
      } else {
        this.destroyPresenceSocketMsg();
      }
    },
  },

  created() {
    if (this.entityId !== null) {
      this.setupPresenceSocketMsg();
    }
  },

  beforeDestroy() {
    this.destroyPresenceSocketMsg();
  },

  methods: {
    /** Send an information to other users indicating the current user's presence */
    setupPresenceSocketMsg() {
      if (this.sendPresenceInterval) {
        return;
      }
      this.handleSocketMessage(SocketMessages.CC_MSGS.SYNC_USER_JOINED, (msg) => {
        if (Number(msg.id) !== this.entityId || msg.type !== this.entityType) return;
        const existing = this.otherUsersInfo.find(info => info.user === msg.user && info.team === msg.team);
        if (existing) {
          clearTimeout(existing.timeout);
          existing.timeout = setTimeout(() => {
            this.removeOtherUserInfo(msg);
          }, 20000);
        } else {
          this.otherUsersInfo.push({
            ...msg,
            timeout: setTimeout(() => {
              this.removeOtherUserInfo(msg);
            }, 20000),
          });
        }
      });

      this.handleSocketMessage(SocketMessages.CC_MSGS.SYNC_USER_LEFT, (msg) => {
        if (Number(msg.id) !== this.entityId || msg.type !== this.entityType) return;
        this.removeOtherUserInfo(msg);
      });

      // Notify presence to other users
      this.sendSocketMessage(this.socketMsg);
      this.sendPresenceInterval = setInterval(() => {
        this.sendSocketMessage(this.socketMsg);
      }, 3000);
    },
    /** Timeout for another user */
    removeOtherUserInfo(msg) {
      const toDelete = this.otherUsersInfo.findIndex(info => info.user === msg.user && info.team === msg.team);
      clearTimeout(this.otherUsersInfo[toDelete].timeout);
      this.otherUsersInfo.splice(toDelete, 1);
      this.otherUsersInfo = this.otherUsersInfo;
    },
    /** Remove presence timeout and intervals */
    destroyPresenceSocketMsg() {
      this.sendSocketMessage({ ...this.socketMsg, msg: SocketMessages.CC_MSGS.SYNC_USER_LEFT });
      clearInterval(this.sendPresenceInterval);
      this.otherUsersInfo.forEach((info) => {
        clearTimeout(info.timeout);
      });
      this.otherUsersInfo = [];
    },

  },

};
</script>

<style lang="scss">
.users-sync {
  &__other-users {
    &__wrapper {
      margin-left: 0.2rem;

      .user-thumbnail {
        margin-right: 0.3rem;
        cursor: initial;
      }
    }

    &__team {
      margin-right: 0.3rem;
      background: #BDBEC0;
      // color: white;
      border-radius: 50px;
      width: 1.8rem;
      height: 1.8rem;
      padding: 0.38rem;
      box-sizing: border-box;

      .icon {
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
